import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  IconAlertCircle,
  IconDeviceFloppy,
  IconX,
} from "@tabler/icons-react";
import { UnassignedTeamsPanel } from "./UnassignedTeamsPanel";
import { useSavePins } from "./useSavePins";
import type {
  SeedingGroup,
  SeedingPinState,
  SeedingTeam,
} from "./seeding.types";

// Re-export so existing external consumers (e.g. StepDashboard) keep working
// while the canonical definition now lives in `seeding.types.ts`.
export type { SeedingPinState } from "./seeding.types";

interface SeedingGroupPinTabProps {
  readonly stepId: string;
  readonly teams: readonly SeedingTeam[];
  readonly groups: readonly SeedingGroup[];
  readonly initialPins: readonly SeedingPinState[];
  readonly onPinsSaved?: (pins: readonly SeedingPinState[]) => void;
}

const UNASSIGNED_DROP_ID = "unassigned";

export const SeedingGroupPinTab = ({
  stepId,
  teams,
  groups,
  initialPins,
  onPinsSaved,
}: SeedingGroupPinTabProps) => {
  // Group-level pin assignments only. Slot-level pins (gameId / slotIndex)
  // are preserved in `nonGroupPins` so they survive a save from this tab.
  const initialAssignments = useMemo(() => {
    const byTeam = new Map<string, string>();
    for (const pin of initialPins) {
      if (pin.groupId && !pin.gameId) byTeam.set(pin.teamId, pin.groupId);
    }
    return byTeam;
  }, [initialPins]);

  const [assignments, setAssignments] =
    useState<Map<string, string>>(initialAssignments);

  useEffect(() => {
    setAssignments(new Map(initialAssignments));
  }, [initialAssignments]);

  const nonGroupPins = useMemo(
    () => initialPins.filter((p) => p.gameId || p.slotIndex != null),
    [initialPins],
  );

  const { save, saving } = useSavePins({
    stepId,
    successTitle: "Group pins saved",
    successMessage: "Teams have been pinned to the selected groups.",
    onSaved: onPinsSaved,
  });

  const hasChanges = useMemo(() => {
    if (assignments.size !== initialAssignments.size) return true;
    for (const [teamId, groupId] of assignments) {
      if (initialAssignments.get(teamId) !== groupId) return true;
    }
    return false;
  }, [assignments, initialAssignments]);

  const unpinnedTeams = useMemo(
    () => teams.filter((t) => !assignments.has(t.id)),
    [teams, assignments],
  );

  const teamsByGroup = useMemo(() => {
    const out = new Map<string, SeedingTeam[]>();
    for (const group of groups) out.set(group.id, []);
    const byTeamId = new Map(teams.map((t) => [t.id, t]));
    for (const [teamId, groupId] of assignments) {
      const team = byTeamId.get(teamId);
      const list = out.get(groupId);
      if (team && list) list.push(team);
    }
    return out;
  }, [teams, groups, assignments]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    const next = new Map(assignments);
    if (destination.droppableId === UNASSIGNED_DROP_ID) {
      next.delete(draggableId);
    } else {
      next.set(draggableId, destination.droppableId);
    }
    setAssignments(next);
  };

  const handleUnpin = (teamId: string) => {
    const next = new Map(assignments);
    next.delete(teamId);
    setAssignments(next);
  };

  const handleSave = async (): Promise<void> => {
    const pins: SeedingPinState[] = [
      ...Array.from(assignments.entries()).map(([teamId, groupId]) => ({
        teamId,
        groupId,
      })),
      ...nonGroupPins,
    ];
    await save(pins);
  };

  if (groups.length === 0) {
    return (
      <Alert
        color="yellow"
        variant="light"
        icon={<IconAlertCircle size={16} />}
      >
        Step must be generated before group pins can be assigned.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={2}>
          <Text fw={600}>Group pins</Text>
          <Text size="xs" c="dimmed">
            Drag teams onto a group to anchor them there during seeding. The
            preset&apos;s repartition algorithm will place the remaining teams
            around the pins.
          </Text>
        </Stack>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          disabled={!hasChanges || saving}
          loading={saving}
          onClick={handleSave}
        >
          Save pins
        </Button>
      </Group>

      <DragDropContext onDragEnd={handleDragEnd}>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <UnassignedTeamsPanel
            droppableId={UNASSIGNED_DROP_ID}
            teams={unpinnedTeams}
            saving={saving}
            maxHeight={380}
            emptyLabel="All teams are pinned to a group."
          />

          {/* Groups */}
          <Stack gap="md">
            {groups.map((group) => {
              const pinnedTeams = teamsByGroup.get(group.id) ?? [];
              return (
                <Card key={group.id} withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text fw={600}>{group.name}</Text>
                      <Badge variant="light" color="cyan">
                        {pinnedTeams.length} pinned
                      </Badge>
                    </Group>
                    <Droppable droppableId={group.id}>
                      {(provided, snapshot) => (
                        <Stack
                          gap="xs"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          mih={80}
                          style={{
                            backgroundColor: snapshot.isDraggingOver
                              ? "var(--mantine-color-cyan-9)"
                              : undefined,
                            borderRadius: 6,
                            padding: 4,
                          }}
                        >
                          {pinnedTeams.map((team, idx) => (
                            <Draggable
                              key={team.id}
                              draggableId={team.id}
                              index={idx}
                              isDragDisabled={saving}
                            >
                              {(drag) => (
                                <Card
                                  withBorder
                                  padding="xs"
                                  ref={drag.innerRef}
                                  {...drag.draggableProps}
                                  style={{
                                    ...drag.draggableProps.style,
                                    cursor: "grab",
                                  }}
                                >
                                  <Group justify="space-between">
                                    <Text
                                      size="sm"
                                      fw={500}
                                      {...drag.dragHandleProps}
                                      style={{ flex: 1 }}
                                    >
                                      {team.name}
                                    </Text>
                                    <ActionIcon
                                      size="sm"
                                      variant="subtle"
                                      color="red"
                                      aria-label={`Unpin ${team.name}`}
                                      onClick={() => handleUnpin(team.id)}
                                    >
                                      <IconX size={14} />
                                    </ActionIcon>
                                  </Group>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {pinnedTeams.length === 0 && (
                            <Text
                              size="xs"
                              c="dimmed"
                              ta="center"
                              py="md"
                            >
                              Drop teams here to pin them to {group.name}.
                            </Text>
                          )}
                        </Stack>
                      )}
                    </Droppable>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </SimpleGrid>
      </DragDropContext>
    </Stack>
  );
};
